const CAROUSEL_SLIDE_PREFIX = 'carousel_slide_'

export function carouselSlideFocusKey(index: number): string {
  return `${CAROUSEL_SLIDE_PREFIX}${index}`
}

export function parseCarouselSlideFocus(focus: string | null | undefined): number | null {
  if (!focus || !focus.startsWith(CAROUSEL_SLIDE_PREFIX)) return null
  const idx = Number(focus.slice(CAROUSEL_SLIDE_PREFIX.length))
  return Number.isInteger(idx) && idx >= 0 ? idx : null
}

export function isCarouselSlideFocus(focus: string | null | undefined): boolean {
  return !!focus && focus.startsWith(CAROUSEL_SLIDE_PREFIX)
}

// ─── Section action labels (hover overlay + mobile action bar) ───────────────

const FIELD_LABELS: Record<string, string> = {
  image: 'Image',
  video: 'Video',
  caption: 'Caption',
  hashtags: 'Hashtags',
  message: 'Message',
  text: 'Text',
  title: 'Title',
  description: 'Description',
  visibility: 'Visibility',
  link: 'Link',
  altText: 'Alt text',
  logo: 'Logo',
  headline: 'Headline',
  subtitle: 'Subtitle',
  tagline: 'Tagline',
  heroImage: 'Hero image',
}

const MEDIA_CHANGE_FIELDS = new Set(['image', 'video', 'heroImage'])

function capitalize(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function fieldLabel(fieldId: string | null | undefined): string {
  const id = fieldId?.trim() ?? ''
  if (!id) return 'Section'
  if (isCarouselSlideFocus(id)) {
    const idx = parseCarouselSlideFocus(id)
    return idx !== null ? `Slide ${idx + 1}` : 'Slide'
  }
  return FIELD_LABELS[id] ?? capitalize(id)
}

/** Human label for a field id (modal titles, hover chips). */
export function getFieldLabelById(fieldId: string | null | undefined): string | null {
  if (!fieldId?.trim()) return null
  return fieldLabel(fieldId)
}

/** Primary edit action on a preview section (e.g. "Change image", "Edit caption"). */
export function getSectionEditActionLabel(fieldId: string | null | undefined): string {
  const id = fieldId?.trim() ?? ''
  if (!id) return 'Edit section'
  if (isCarouselSlideFocus(id)) {
    return 'Change slide'
  }
  const label = fieldLabel(id)
  if (MEDIA_CHANGE_FIELDS.has(id)) {
    return `Change ${label.toLowerCase()}`
  }
  return `Edit ${label.toLowerCase()}`
}

/** Ask-AI action scoped to a preview section. */
export function getSectionAskActionLabel(fieldId: string | null | undefined): string {
  const id = fieldId?.trim() ?? ''
  if (!id) return 'Ask AI'
  if (isCarouselSlideFocus(id)) {
    return 'Ask AI about slide'
  }
  const label = fieldLabel(id)
  return `Ask AI about ${label.toLowerCase()}`
}
